import { useState, useEffect, useCallback } from 'react';
import { Minus, Square, Copy, X } from 'lucide-react';
import { usesCustomWindowControls, getDesktopWindow } from '../../utils/desktopWindow';
import './WindowControls.css';

/**
 * WindowControls — minimise / maximise / close for the undecorated desktop window.
 *
 * Renders NOTHING in the browser (the OS still owns the frame) and nothing on
 * macOS (real traffic lights are overlaid there via titleBarStyle:"Overlay").
 *
 * The window handle comes from `getDesktopWindow()` rather than importing
 * `@tauri-apps/api` here: the import stays lazy (the web bundle never pulls it)
 * and there is one seam to point at instead of two.
 */
export default function WindowControls() {
    const enabled = usesCustomWindowControls();
    const [maximized, setMaximized] = useState(false);

    useEffect(() => {
        if (!enabled) return undefined;
        let unlisten;
        let alive = true;

        (async () => {
            const win = await getDesktopWindow();
            const sync = async () => {
                const is = await win.isMaximized();
                if (alive) setMaximized(is);
            };
            await sync();
            // Snap, double-click on the drag region and the OS shortcut all change
            // maximised state without going through our button.
            unlisten = await win.onResized(sync);
        })();

        return () => {
            alive = false;
            if (typeof unlisten === 'function') unlisten();
        };
    }, [enabled]);

    const act = useCallback(async (method) => {
        const win = await getDesktopWindow();
        await win[method]();
    }, []);

    if (!enabled) return null;

    return (
        <div className="window-controls">
            <button
                type="button"
                className="window-controls__btn"
                onClick={() => act('minimize')}
                aria-label="Minimise"
                title="Minimise"
            >
                <Minus size={15} />
            </button>
            <button
                type="button"
                className="window-controls__btn"
                onClick={() => act('toggleMaximize')}
                aria-label={maximized ? 'Restore' : 'Maximise'}
                title={maximized ? 'Restore' : 'Maximise'}
            >
                {maximized ? <Copy size={13} /> : <Square size={13} />}
            </button>
            <button
                type="button"
                className="window-controls__btn window-controls__btn--close"
                onClick={() => act('close')}
                aria-label="Close"
                title="Close"
            >
                <X size={16} />
            </button>
        </div>
    );
}
